// Bijlagen en gesprek: de bestanden en links uit Planner (met leesbare naam en soort), het Teams-gesprek openen of
// starten, een link toevoegen of weghalen, en een bestand toevoegen zoals in Planner: slepen of kiezen. Het bestand gaat
// namens de gebruiker naar de SharePoint-site van het plan (de bibliotheek van zijn Microsoft 365-groep, waar Planner
// het ook zet) en hangt dan als bijlage aan de taak; weghalen haalt enkel de bijlage weg. Gedeeld door de taaklade en
// de taakpagina.
import { useRef, useState, type DragEvent } from "react";
import { usePlanning, useTaskOps } from "../../lib/hooks";
import { Icon } from "../../lib/icons";
import { dmy } from "../../lib/planning";
import type { Attachment, Task, TaskOp } from "../../lib/types";
import { useToast } from "../../shell/Toast";
import type { TaskPerm } from "./perm";

// Een bijlage in Planner: een bestand (naam met extensie, adres naar SharePoint) of een link (adres, soms met een naam).
export function attView(a: Attachment) {
  const isUrl = (s: string) => /^https?:\/\//i.test(s);
  const label = a.name || "", uri = a.uri || (isUrl(label) ? label : "");
  const ext0 = (/\.([a-z0-9]+)$/i.exec(label)?.[1] || "").toLowerCase();
  if (!isUrl(label) && (a.type !== "Other" || ext0 || !uri)) {
    const ext = ({ docx: "W", doc: "W", xlsx: "X", xls: "X", pdf: "PDF", xml: "XML", txt: "TXT", html: "HTM" } as Record<string, string>)[ext0] || (ext0 ? ext0.toUpperCase().slice(0, 4) : "FILE");
    return { name: label, sub: ext0 ? ext0.toUpperCase() + "-bestand" : "bestand", ext, cls: "p-att-ic" + (({ W: " w", X: " x", PDF: " pdf" } as Record<string, string>)[ext] || ""), href: uri || null, link: false };
  }
  const raw = uri;
  let host = "";
  try { host = new URL(raw).host.replace(/^www\./, ""); } catch { /* geen geldige URL */ }
  const sp = /sharepoint\.com$/i.test(host), site = /\/sites\/([^/]+)/.exec(raw)?.[1];
  let seg = "";
  try { seg = decodeURIComponent(raw.split("?")[0]).split("/").filter(Boolean).pop() || ""; } catch { /* laat leeg */ }
  const kind = /\/:w:\//.test(raw) ? "Word-document" : /\/:x:\//.test(raw) ? "Excel-bestand" : /\/:f:\//.test(raw) ? "Map" : /AllItems\.aspx$/i.test(seg) ? "Documenten" : "Link";
  const name = !isUrl(label) && label ? label : /\.aspx$/i.test(seg) || !seg ? kind + (sp ? " in SharePoint" : "") : seg;
  return { name, sub: sp ? "SharePoint" + (site ? " · " + decodeURIComponent(site) : "") : host, ext: sp ? "SP" : "WWW", cls: "p-att-ic" + (sp ? " sp" : ""), href: raw, link: true };
}

/** Een bestand dat onderweg is: eerst naar de server (voortgang), dan zet die het in SharePoint en aan de taak. */
interface Upload { key: number; name: string; frac: number; error?: string }

const why = (e: unknown) => (e instanceof Error ? e.message : "er ging iets mis");
const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

export function AttachmentsSection({ task, perm, run, page }: { task: Task; perm: TaskPerm; run: (ops: TaskOp[], message: string) => void; page?: boolean }) {
  const toast = useToast();
  const ops = useTaskOps();
  const { data } = usePlanning();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const seq = useRef(0);
  const pick = useRef<HTMLInputElement>(null);
  const edit = perm.edit && !task.done;
  // bestanden kunnen enkel naar een plan met een Microsoft 365-groep (het sjabloonplan heeft er geen)
  const canUpload = edit && !!data?.klanten.find((k) => k.id === task.projectId)?.groupId;
  const add = () => {
    const raw = url.trim(), n = name.trim();
    if (!raw) { toast.flash("Geef het adres van de link", { error: true }); return; }
    const href = /^https?:\/\//i.test(raw) ? raw : "https://" + raw;
    setOpen(false); setUrl(""); setName("");
    run([{ kind: "link", url: href, name: n, on: true }], "Link toegevoegd aan " + task.title + ": " + (n || href));
  };
  const patch = (key: number, p: Partial<Upload>) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const addFiles = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const key = ++seq.current;
      setUploads((u) => [...u, { key, name: file.name, frac: 0 }]);
      ops.upload(task, file, (frac) => patch(key, { frac }))
        .then(() => setUploads((u) => u.filter((x) => x.key !== key)), (e) => patch(key, { error: why(e) }));
    }
  };
  // slepen: het hele vak licht op zolang er bestanden boven hangen (enter en leave komen per kindelement)
  const drag = canUpload ? {
    onDragEnter: (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth.current++; setOver(true); },
    onDragOver: (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; },
    onDragLeave: () => { if (--depth.current <= 0) { depth.current = 0; setOver(false); } },
    onDrop: (e: DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); depth.current = 0; setOver(false); addFiles(e.dataTransfer.files); },
  } : {};
  return (
    <div className={"p-attzone" + (over ? " over" : "")} {...drag}>
      {page ? <div className="p-tk-boxh"><h3>Bijlagen en gesprek</h3>{!canUpload && <span className="p-kd-note" style={{ margin: 0 }}>bestanden toevoegen doet u in Planner</span>}</div> : <h3>Bijlagen en gesprek</h3>}
      {task.attachments.map((a) => { const v = attView(a); return (
        <div key={a.id} className="p-att"><span className={v.cls}>{v.ext}</span><span className="t"><b title={a.name}>{v.name}</b><small>{v.sub}</small></span>
          <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {v.href ? <a className="ex-btn-xs" href={v.href} target="_blank" rel="noreferrer">Openen</a> : <span className="t-mute" style={{ fontSize: 12 }}>in Planner</span>}
            {edit && v.link && v.href && <button type="button" className="ex-icon-btn ex-icon-btn--square p-dep-x" aria-label="Link weghalen" title="Link weghalen" onClick={() => run([{ kind: "link", url: v.href!, name: a.name, on: false }], "Link weggehaald: " + v.name)}><Icon name="x" size={14} /></button>}
          </span>
        </div>); })}
      {uploads.map((u) => { const v = attView({ id: "upload", name: u.name, type: "Other" }); return (
        <div key={u.key} className="p-att p-att-up"><span className={v.cls}>{v.ext}</span>
          <span className="t"><b title={u.name}>{u.name}</b>
            {u.error ? <small className="t-err">{u.error}</small> : <><small>{u.frac < 1 ? "opladen · " + Math.round(u.frac * 100) + " %" : "in SharePoint zetten…"}</small><span className="p-att-prog" aria-hidden="true"><i style={{ width: Math.round(u.frac * 100) + "%" }} /></span></>}
          </span>
          {u.error ? <button type="button" className="ex-icon-btn ex-icon-btn--square p-dep-x" aria-label="Melding weghalen" title="Melding weghalen" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))}><Icon name="x" size={14} /></button> : <span className="t-mute" style={{ fontSize: 12 }}>bezig</span>}
        </div>); })}
      {!task.attachments.length && !uploads.length && <p className="p-kd-note" style={{ margin: "0 0 4px" }}>Nog geen bijlagen.</p>}
      <div className="p-att p-att-chat"><span className="p-att-ic chat"><Icon name="chat" size={16} /></span>
        <span className="t"><b>{task.conversation ? "Gesprek in Teams" : "Nog geen gesprek in Teams"}</b><small>{task.conversation ? "gestart op " + dmy(task.conversation.since) + " · de berichten staan in Teams" : "Start er een vanuit de taak in Planner; het blijft aan de taak hangen"}</small></span>
        <a className="ex-btn-xs" href={perm.url} target="_blank" rel="noreferrer">{task.conversation ? "Openen" : "Starten"}</a>
      </div>
      {canUpload && (
        <div className="p-drop">
          <Icon name="clip" size={14} />
          <span>Sleep bestanden hierheen of <button type="button" className="p-drop-pick" onClick={() => pick.current?.click()}>kies een bestand</button></span>
          <input ref={pick} type="file" multiple hidden aria-label="Bestand kiezen" onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
        </div>
      )}
      {edit && <div className="p-quick" style={{ marginTop: 10 }}><button type="button" className="p-tog" aria-expanded={open} onClick={() => { setOpen(!open); setUrl(""); setName(""); }}><Icon name="plus" size={13} />Link toevoegen</button></div>}
      {open && (
        <div className="p-attform">
          <input className="ex-input" type="text" placeholder="https://…" aria-label="Adres van de link" value={url} autoFocus onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <input className="ex-input" type="text" placeholder="Naam, bijvoorbeeld specificatie klant" aria-label="Naam van de link" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={add}>Toevoegen</button>
          <button type="button" className="ex-btn-xs" onClick={() => setOpen(false)}>Annuleren</button>
        </div>
      )}
    </div>
  );
}
